<template>
  <v-container class="pa-0 ma-0">
    <v-layout wrap>
      <v-row>
        <div class="page__title">
          <h1>{{ event.name }}</h1>
        </div>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <div class="event-images">
            <img
              class="event-background"
              :alt="'Zdjęcie usługi: ' + event.name"
              :src="event.mainImage ? mediaUrl + event.mainImage : '/img/no-image.png'"
            />
          </div>

          <div role="description" aria-level="3" class="event-description" v-html="event.description" />
        </v-col>

        <v-col cols="12" md="6">
          <v-card class="v-card--no-style separate--after separate--before--md">
            <div role="heading" aria-level="2" class="v-card-h1">Szczegóły</div>

            <ul class="tags-container mb-8 mt-4 px-0">
              <li v-if="!event.shared" role="region" aria-label="Miejsce usługi" class="tag">
                <v-icon class="mr-4 lighten-2">mdi-map-marker-outline</v-icon>

                <p class="sr-only">Miejsce usługi</p>

                <p>{{ place }}</p>
              </li>

              <li v-if="event.start && event.end" role="region" aria-label="Data usługi" class="tag">
                <v-icon class="mr-4 lighten-2">mdi-calendar</v-icon>

                <p class="sr-only">Data usługi</p>

                <p>{{ event.start | dateFilter }} - {{ event.end | dateFilter }}</p>
              </li>

              <li role="region" aria-label="Typ zniżki" class="tag">
                <v-icon class="mr-4 lighten-2">{{ event.type | benefitIconType }}</v-icon>

                <p class="sr-only">Typ zniżki</p>

                <p>
                  <b>
                    {{ event.type | benefitType }}:
                    <span>{{ event | benefitValue }}</span>
                  </b>
                </p>
              </li>

              <li role="region" aria-label="Grupy" class="tag">
                <v-icon class="mr-4 lighten-2">mdi-account-group</v-icon>

                <p class="sr-only">Grupy</p>

                <p>
                  <b>
                    Grupy:
                  </b>

                  <span>
                    {{ groups }}
                  </span>
                </p>
              </li>

              <template v-if="config.modules.points">
                <li v-if="event.pointContainer && event.pointCost" role="region" class="tag">
                  <v-icon class="mr-4 lighten-2">mdi-shopping</v-icon>

                  <p>
                    <b> Wymagana liczba punktów w {{ event.pointContainer.name }}: </b>

                    <span>
                      {{ event.pointCost }}
                    </span>

                    <span v-if="event.userPoints"> (posiadasz: {{ event.userPoints ? event.userPoints : 0 }}) </span>
                  </p>
                </li>

                <template v-if="event.containerBenefit && event.containerBenefit.length">
                  <li role="region" class="tag">
                    <v-icon class="mr-4 lighten-2">mdi-shopping</v-icon>

                    <p>
                      <b> Możliwe punkty do zdobycia: </b>
                    </p>
                  </li>

                  <li
                    v-for="(containerBenefit, index) in event.containerBenefit"
                    :key="'containerBenefit_' + index"
                    role="region"
                    style="list-style: none;"
                    class="tag ml-12"
                  >
                    <p>{{ containerBenefit.pointContainer.name }}: {{ containerBenefit.points }}</p>
                  </li>
                </template>
              </template>
            </ul>
          </v-card>

          <v-card class="v-card--no-style mt-6">
            <div role="heading" aria-level="2" class="v-card-h1">{{ event.shared ? 'Partnerzy' : 'Partner' }}</div>

            <div v-for="(partner, index) in partners" :key="'partner_' + index" class="event__partner-container">
              <img
                class="partner-avatar"
                alt="Zdjęcie partnera"
                :src="partner.mainImage ? mediaUrl + partner.mainImage : '/img/no-image.png'"
              />

              <ul class="tags-container ml-4 px-0">
                <li role="region" aria-label="Nazwa partnera" class="tag">
                  <v-icon class="mr-4 lighten-2">mdi-storefront</v-icon>

                  <p class="sr-only">Nazwa partnera</p>

                  <router-link :to="{ name: 'partnerDetails', params: { id: partner.id } }">
                    {{ partner.name }}
                  </router-link>
                </li>

                <li v-if="partner.phoneNumber" role="region" aria-label="Numer telefonu" class="tag">
                  <v-icon class="mr-4">mdi-phone</v-icon>

                  <p class="sr-only">Numer telefonu partnera</p>

                  <p>{{ partner.phoneNumber }}</p>
                </li>

                <li v-if="partner.email" role="region" aria-label="Email partnera" class="tag">
                  <v-icon class="mr-4">mdi-email</v-icon>

                  <p class="sr-only">E-mail partnera</p>

                  <a :href="'mailto:' + partner.email">{{ partner.email }}</a>
                </li>

                <li v-if="partner.website" role="region" aria-label="Strona partnera" class="tag">
                  <v-icon class="mr-4">mdi-link-variant</v-icon>

                  <p class="sr-only">Strona internetowa partnera</p>

                  <a :href="getWebsiteUrl(partner)">{{ partner.website }}</a>
                </li>
              </ul>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <gallery v-if="event.images" :images="images(event.images)" />
    </v-layout>
  </v-container>
</template>

<script>
import { mapState } from 'vuex';
import {
  address,
  benefitIconType,
  benefitType,
  benefitValue,
  dateFilter,
  PublicSettings
} from '@witkac/kartamiejska-shared';

import Gallery from '@/components/gallery/Gallery';
import { imagesMixin } from '@/mixins/images';
import { mediaMixin } from '@/mixins/media';
import { config } from '@/utils/config';

// @vuese
// Szczegóły wydarzenia
// @group Event
export default {
  components: { Gallery },
  filters: {
    address,
    benefitIconType,
    benefitType,
    benefitValue,
    dateFilter
  },
  mixins: [mediaMixin, imagesMixin],
  data() {
    return { config };
  },
  computed: {
    ...mapState({
      event: state => state.benefit.data,
      noGroupSetting: state => state.settings[PublicSettings.NO_GROUP_SELECTED_NAME].value,
      partners: state => (state.benefit.data.shared ? state.benefit.data.partners : [state.benefit.data.partner]),
      place: state => {
        const place = state.place.data;
        if (place) {
          return address(place);
        }
        return 'Usługa dostępna we wszystkich obiektach';
      }
    }),
    groups() {
      return this.event.groups.length ? this.event.groups.map(item => item.name).join(', ') : this.noGroupSetting;
    }
  },
  methods: {
    // @vuese
    // Pokaż szczegóły
    goToDetails() {
      this.$router.push({
        name: 'eventDetails',
        params: { id: this.event.id }
      });
    },
    // @vuese
    // Link do strony
    getWebsiteUrl(partner) {
      if (partner.website.match(/^https?:\/\//gi)) {
        return partner.website;
      }
      return 'http://' + partner.website;
    }
  }
};
</script>
